import { ChangeEvent, useEffect, useState } from 'react';
import {
  Button,
  DropdownFlags,
  FlexContainer,
  Label,
  RadioButton,
  RadioGroup,
  Spinner,
  useDeviceSize,
  useToast,
} from '@facephi/ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  RequestMethods,
  useLogger,
  useRequest,
  useVariables,
} from '../../hooks';
import { useAuth, useData } from '../../providers';
import {
  ModelingList,
  ServiceDto,
  ServiceSchema,
  RoutesName,
} from '../../states';
import {
  documentTypeState,
  countryState,
  operationIdState,
  antifraudState,
  documentValidationState,
  governmentValidationState,
  livenessValidationState,
  previousOnboardingsValidationState,
  recordOnboardingState,
  signState,
  signTypeState,
  newOperationIdState,
} from '../../states/atoms';
import {
  AuthName,
  DEFAULT_COUNTRY,
  FrontToBackTypeDocument,
  endPoints,
  TypeDocument,
} from '../../states/constants';
import { generateConfigObject, getCountryISO3 } from '../../utils';
import { ServiceSection } from './ServiceSection';
import { ServicesWrapper } from './Styles';

export const Services = () => {
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const { isMobile } = useDeviceSize();
  const [loading, setLoading] = useState<boolean>(false);
  const [documentType, setDocumentType] = useRecoilState(documentTypeState);
  const [country, setCountry] = useRecoilState(countryState);
  const setOperationId = useSetRecoilState(operationIdState);
  const [newOperationId, setNewOperationId] =
    useRecoilState(newOperationIdState);
  const signature = useRecoilValue(signState);
  const signatureType = useRecoilValue(signTypeState);
  const previousOnboardings = useRecoilValue(
    previousOnboardingsValidationState
  );
  const governmentalValidation = useRecoilValue(governmentValidationState);
  const blacklistValidation = useRecoilValue(antifraudState);
  const [documentValidation, setDocumentValidation] = useRecoilState(
    documentValidationState
  );
  const liveness = useRecoilValue(livenessValidationState);
  const recordOnboarding = useRecoilValue(recordOnboardingState);
  const { email, tenantDefault } = useAuth();
  const { demoBackend, appTesting, isCanada } = useVariables();
  const { toastManager } = useToast();
  const { captureException, setTag } = useLogger();
  const { connect: connectSSE, resetStates } = useData();
  const { request: startWorkflowRequest } = useRequest(demoBackend);
  const documentsList = isCanada
    ? {
        [TypeDocument.custom]: { ...ModelingList.Custom },
      }
    : ModelingList;

  useEffect(() => {
    setOperationId('');
    setNewOperationId('');
  }, [setOperationId]);

  useEffect(() => {
    if (isCanada) setDocumentValidation(isCanada);
  }, [isCanada]);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<ServiceDto>({
    resolver: yupResolver(ServiceSchema),
    defaultValues: {
      document: documentType,
      country: DEFAULT_COUNTRY,
    },
  });

  const getNewOperationId = () => {
    if (!newOperationId) {
      const configurationObject = generateConfigObject({
        customerId: email,
        document: !getCountryISO3(country)
          ? undefined
          : {
              country: getCountryISO3(country),
              type: FrontToBackTypeDocument[documentType] || '',
              validateGeolocation: false,
            },
        recording: recordOnboarding,
        signature,
        signatureType,
        previousOnboardings,
        governmentalValidation,
        blacklistValidation,
        documentValidation,
        liveness,
        email,
        tenantDefault,
      });

      startWorkflowRequest(endPoints.Workflows.Start, {
        method: RequestMethods.post,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
          'x-demo-app-version': '1',
          'x-source': 'web',
        },
        data: {
          ...configurationObject,
        },
      })
        .then((response) => {
          setNewOperationId(response.operationId);
          setTag('operationId', response.operationId);
          !appTesting && connectSSE(response.operationId);
          navigate(RoutesName.workflow);
          resetStates();
          setLoading(false);
        })
        .catch((error) => {
          captureException(error as Error, { operation: 'Start Workflow' });
          toastManager({
            duration: 3500,
            type: 'error',
            canClose: true,
            message: t('Start workflow error'),
            testId: 'toast-start-workflow-error',
          });
          setLoading(false);
        });
    }
  };

  const onSubmit = () => {
    setLoading(true);
    getNewOperationId();
  };

  const handleCountryChange = (value: string) => {
    if (country !== value) {
      setCountry(value as string);
    }
  };

  return (
    <FlexContainer alignItems="center">
      <ServicesWrapper
        flexDirection="column"
        rowGap={isMobile ? '3' : '2.5'}
        alignItems="center"
        flex="1"
      >
        <Label
          fontSize={isMobile ? '18' : '32'}
          semibold
          data-test="main_view_title_label"
          id="main_view_title_label"
        >
          {t('Make your onboarding')}
        </Label>
        <FlexContainer
          as="form"
          flexDirection="column"
          rowGap={isMobile ? '2.2' : '2.5'}
          alignItems="center"
          flex="1"
          onSubmit={handleSubmit(onSubmit)}
        >
          {/* Country */}
          <ServiceSection
            iconName="Globe"
            testName="main_view_country_selector_icon"
          >
            <Label
              fontSize={isMobile ? '16' : '18'}
              semibold
              id="main_view_country_selector_label"
              testId="main_view_country_selector_label"
            >
              {t('Which country is your document from')}
            </Label>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <DropdownFlags
                  {...field}
                  testId="service-country"
                  id="main_view_country_selector_text"
                  disabled={isCanada}
                  locale={i18n.language}
                  onChange={handleCountryChange}
                  errorLabel={
                    (errors.country?.message && t(errors.country?.message)) ||
                    ''
                  }
                  value={country}
                  customOptions={[
                    {
                      icon: 'MapPin',
                      name: t('Autodetect'),
                      value: 'XXX',
                    },
                  ]}
                />
              )}
            />
          </ServiceSection>
          {/* Document type */}
          <ServiceSection
            iconName="IdentificationCard"
            testName="main_view_document_selector_icon"
          >
            <Label
              fontSize={isMobile ? '16' : '18'}
              semibold
              id="main_view_document_selector_label"
              testId="main_view_document_selector_label"
            >
              {t('Which document will you use')}
            </Label>
            <RadioGroup
              {...register('document')}
              name="document"
              defaultSelected={documentType}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setDocumentType(e.currentTarget.value as TypeDocument)
              }
              testId="services-documents"
              id="main_view_document_selector_text"
            >
              {Object.entries(documentsList).map(([id, item]) => (
                <RadioButton
                  key={id}
                  label={t(item.title)}
                  value={id}
                  disabled={!item.active}
                />
              ))}
            </RadioGroup>
          </ServiceSection>
          <Button
            size="L"
            fullWidth
            type="submit"
            disabled={!country || loading}
            testId="main_view_continue_button"
            color="primary"
          >
            {loading ? (
              <Spinner insideComponent size="S" />
            ) : (
              t('Continue').toUpperCase()
            )}
          </Button>
        </FlexContainer>
      </ServicesWrapper>
    </FlexContainer>
  );
};
